import React, { useCallback, useMemo } from 'react';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import {
	ChildrenController,
	OPTIMISTIC_ISSUE_ID,
	isGroupItem,
} from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	ListViewChildQueryController,
	ListViewChildQueryBulkController,
} from '../../children-query';
import { useHierarchyStore } from '../hierarchy-store';
import { useSetChildrenItems } from '../set-children-items';

export const useChildrenLoader = () => {
	const [{ expandedItems, childrenItems, issuesMap }] = useHierarchyStore();
	const { data } = useIssueTypesAndFields({ issueOperation: 'VIEW' });

	const { setChildrenItems } = useSetChildrenItems();

	const getHierarchyLevel = useCallback(
		(issueId: string): number | undefined => {
			const maybeIssue = issuesMap.get(issueId);
			if (!maybeIssue) {
				return undefined;
			}
			const maybeIssueTypeData = data.issueTypes.find(
				(issueType) => issueType.id === maybeIssue.issuetype.id,
			);
			if (!maybeIssueTypeData) {
				return undefined;
			}

			return maybeIssueTypeData.hierarchyLevel;
		},
		[data.issueTypes, issuesMap],
	);

	const childrenLoader = useMemo(() => {
		const expandedItemIssueIds = Object.keys(expandedItems).filter(
			(issueId) =>
				!isGroupItem(issueId) &&
				// exclude optimistically created items that'd immediately be expanded when "Expand All" is on
				String(issueId) !== String(OPTIMISTIC_ISSUE_ID),
		);

		const featureFlaggedProps = ff('jwm.remove-list-view-bulk-query')
			? {
					ChildQueryController: ListViewChildQueryController,
				}
			: {
					ChildQueryControllerBulk: ListViewChildQueryBulkController,
				};

		return (
			<ChildrenController
				{...featureFlaggedProps}
				expandedItemIssueIds={expandedItemIssueIds}
				loadingIssueIds={expandedItemIssueIds.filter(
					(expandedItemId) =>
						!childrenItems[expandedItemId] || childrenItems[expandedItemId].loading,
				)}
				setChildrenQueryData={setChildrenItems}
				getHierarchyLevel={getHierarchyLevel}
			/>
		);
	}, [expandedItems, setChildrenItems, getHierarchyLevel, childrenItems]);

	return {
		childrenLoader,
	};
};
